<script lang="ts" setup>
  import { Editor, EditorContent } from '@tiptap/vue-3'
  import {
    FontFamily,
    LineHeight,
    Link,
    RequiredExtensions,
    TextAlign,
    TextBlocks,
    TextSize,
    Underline,
    Color,
  } from '@gohighlevel/ghl-text-editor'
  import Document from '@tiptap/extension-document'
  import Gapcursor from '@tiptap/extension-gapcursor'
  import Paragraph from '@tiptap/extension-paragraph'
  import Table from '@tiptap/extension-table'
  import TableCell from '@tiptap/extension-table-cell'
  import TableHeader from '@tiptap/extension-table-header'
  import TableRow from '@tiptap/extension-table-row'
  import Text from '@tiptap/extension-text'
  import TextStyle from '@tiptap/extension-text-style'
  import StarterKit from '@tiptap/starter-kit'
  import { PropType, computed, reactive, ref, watch, onMounted, onUnmounted } from 'vue'
  import {
    MagicPlaceholderExtension,
    ELEMENTS_META,
    IElement,
    Storage,
    CustomLineHeight,
    extractFontFamiliesFromJSON,
    FontFamilyV2,
    ExtendedHeading,
    InsertGapOnPaste,
  } from '@gohighlevel/ghl-proposals-common'
  import { columnResizing, tableEditing } from '@tiptap/pm/tables'
  import { EditorState } from '@tiptap/pm/state'
  import { useElementOption } from '@/composable/elementOptions'
  import { useDocument } from '@/composition'
  import { useBuilderStore } from '@/store/builder'
  import { TableCellAddAttributes } from '@/util/tableCellExtend'
  import TableMenuComponent from '../common/TableMenuComponent.vue'
  import BulletList from '@tiptap/extension-bullet-list'

  const editor = ref()
  const store = useBuilderStore()
  const isEditable = computed(() => store.isDocumentEditable)
  const { loadingCustomValues, documentVersion } = useDocument()

  const props = defineProps({
    element: {
      type: Object as PropType<IElement>,
      required: true,
    },
    customValues: {
      type: Object as PropType<Record<string, string>>,
      required: true,
    },
  })

  const { updateOption } = useElementOption()

  const CustomBulletList = BulletList.extend({
    addCommands() {
      return {
        ...this.parent?.(),
        toggleCustomBulletList: () => editor => {
          return editor.commands.toggleBulletList()
        },
      }
    },
  })

  editor.value = new Editor({
    extensions: [
      Document,
      Paragraph,
      Text,
      MagicPlaceholderExtension.configure({
        placeholderValues: props.customValues,
        loading: loadingCustomValues.value,
      }),
      Gapcursor,
      Table.configure({
        resizable: true,
      }).extend({
        addProseMirrorPlugins() {
          return [
            columnResizing({
              handleWidth: this.options.handleWidth,
              cellMinWidth: this.options.cellMinWidth,
              View: this.options.View,
              lastColumnResizable: this.options.lastColumnResizable,
            }),
            tableEditing({
              allowTableNodeSelection: this.options.allowTableNodeSelection,
            }),
          ]
        },
      }),
      TableRow,
      TableHeader,
      TableCell.extend(TableCellAddAttributes),
      RequiredExtensions,
      TextBlocks,
      ...((documentVersion.value ?? 1) > 1 ? [ExtendedHeading] : []), // don't change the order, this should be added after TextBlocks
      StarterKit,
      TextStyle,
      FontFamily,
      ...((documentVersion.value ?? 1) === 1 ? [FontFamily] : []),
      ...((documentVersion.value ?? 1) > 1 ? [FontFamilyV2] : []),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Link,
      // Replace the LineHeight extension with CustomLineHeight
      ...((documentVersion.value ?? 1) > 1
        ? [
            CustomLineHeight.configure({
              defaultLineHeight: 1.5,
              types: ['paragraph', 'heading', 'blockquote', 'listItem'],
            }),
          ]
        : []),
      ...((documentVersion.value ?? 1) === 1 ? [LineHeight] : []),
      TextSize,
      Color,
      CustomBulletList,
      Storage.configure({
        storage: {
          version: props.element?.version ?? 1,
          documentVersion: documentVersion.value ?? 1,
        },
      }),
      ...((documentVersion.value ?? 1) > 1 ? [InsertGapOnPaste] : []),
    ],
    onUpdate({ editor }) {
      updateOption(ELEMENTS_META.TEXT, editor.getHTML(), props?.element?.id, props?.element?.type)
    },
    autofocus: false,
    content: props.element.component.options.text,
    editable: isEditable.value,
    parseOptions: {
      preserveWhitespace: true,
    },
    editorProps: {
      attributes: (state: EditorState) => {
        return {
          ...state.doc.attrs,
          ...((documentVersion.value ?? 1) === 1 ? { style: 'font-family: sans-serif' } : {}),
          ...(documentVersion.value > 1 ? { style: 'font-family: Open Sans' } : {}),
        }
      },
    },
  })
  watch(isEditable, () => {
    editor.value.setEditable(isEditable.value)
  })

  watch(
    () => store.moveElementDragState,
    val => {
      editor.value.setEditable(!val)
    }
  )

  // Sync the tiptap editor's internal content with `options.text` whenever the store signals a
  // refresh (undo/redo, header/footer propagation from another page). Without this watcher, the
  // editor keeps the content it was instantiated with — so after a table cell edit on the host
  // page propagates to cloned pages, the data is correct on those pages but the tiptap editors
  // still render the old HTML because tiptap doesn't react to content-prop changes automatically.
  // `flush: 'post'` is critical here: default `pre` runs the watcher BEFORE the parent re-renders
  // with the newly-cloned element, so `props.element.component.options.text` read at that moment
  // is the PREVIOUS value — producing a one-step lag where non-host pages always showed the
  // last-but-one edit instead of the most recent.
  const previousUpdateEditors = ref(store.updateEditors)
  watch(
    () => store.updateEditors,
    newValue => {
      if (newValue !== previousUpdateEditors.value && props.element?.component?.options?.text !== undefined) {
        const currentContent = editor.value?.getHTML()
        const newContent = props.element.component.options.text
        if (currentContent !== newContent) {
          editor.value?.commands.setContent(newContent)
        }
      }
      previousUpdateEditors.value = newValue
    },
    { deep: true, flush: 'post' }
  )

  function onTableCellEditorFocus() {
    store.updateEditorInstance(editor.value)
  }

  function onTableCellEditorBlur() {
    const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
    store.setLoadedFontsFromEditor(Array.from(fonts))
  }

  function onTableCellEditorCreate() {
    const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
    store.setLoadedFontsFromEditor(Array.from(fonts))
  }

  onMounted(() => {
    editor.value.on('focus', onTableCellEditorFocus)
    if (documentVersion.value > 1) {
      editor.value.on('blur', onTableCellEditorBlur)
      editor.value.on('create', onTableCellEditorCreate)
    }
  })

  onUnmounted(() => {
    editor.value?.off('focus', onTableCellEditorFocus)
    if (documentVersion.value > 1) {
      editor.value?.off('blur', onTableCellEditorBlur)
      editor.value?.off('create', onTableCellEditorCreate)
    }
    editor.value?.destroy()
  })

  const activeElementId = computed(() => store.activeElementId)

  const state = reactive({
    isActiveElement: computed(() => {
      if (!props.element) return false
      return activeElementId.value === props.element.id
    }),
  })

  const isVersionGreaterThanOne = computed(() => {
    return documentVersion.value > 1
  })
</script>

<template>
  <TableMenuComponent v-if="state.isActiveElement" :editor="editor" />
  <div class="w-full overflow-auto">
    <EditorContent :editor="editor" :class="{ 'document-version-gt-1': isVersionGreaterThanOne }" />
  </div>
</template>
